import { useTranslation } from "react-i18next"
import { alpha, Skeleton, Stack, Typography } from "@mui/material"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"

import type { EntityType } from "types/common.types"

import useDevices from "helpers/hooks/useDevices"
import usePowerline from "helpers/hooks/powerlines/usePowerline"
import useTower from "helpers/hooks/towers/useTower"
import { nameOrUnnamed } from "helpers/utils/translations"
import EntityIcon from "widgets/common/EntityIcon"

interface AlertSettingEntityCardProps {
  entity: EntityType
  entityId: number
}

// The entity the setting belongs to, which the form can't change.
const AlertSettingEntityCard = ({ entity, entityId }: AlertSettingEntityCardProps) => {
  const { t } = useTranslation()
  const { devsWRelsById, isLoading: devicesLoading } = useDevices({
    skip: entity !== "device",
  })
  const { powerline, isLoading: powerlineLoading } = usePowerline({
    id: entity === "powerline" ? entityId : null,
  })
  const { tower, isLoading: towerLoading } = useTower({
    id: entity === "tower" ? entityId : null,
  })
  const name =
    entity === "powerline"
      ? powerline?.name
      : entity === "tower"
        ? tower?.name
        : devsWRelsById?.[entityId]?.device.name

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{
        px: 1.5,
        py: 1.25,
        border: 1,
        borderColor: "neutral.300",
        borderRadius: 1,
        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.03),
      }}
    >
      <EntityIcon entity={entity} />
      <Stack flex={1} minWidth={0} gap={0.25}>
        <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
          {devicesLoading || powerlineLoading || towerLoading ? (
            <Skeleton width="40%" />
          ) : (
            nameOrUnnamed(name, t)
          )}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {t(`entities.${entity.toUpperCase()}`)} · {t("generic.ID")} {entityId}
        </Typography>
      </Stack>
      <LockOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
    </Stack>
  )
}

export default AlertSettingEntityCard
